import { COLORS, BG_BASE } from "../../utils/colors";
import { ClickableName } from "../common/EmployeeNameLink";

// Compute week-end date labels starting from a given date string.
// e.g. startDate "2024-08-26" → ["Sep 1", "Sep 8", "Sep 15", ...]
function computeWeekLabels(startDateStr, nWeeks = 12) {
  if (!startDateStr) return Array.from({ length: nWeeks }, (_, i) => `W${i + 1}`);
  const start = new Date(startDateStr + "T00:00:00");
  return Array.from({ length: nWeeks }, (_, i) => {
    const d = new Date(start);
    d.setDate(d.getDate() + (i + 1) * 7 - 1);
    return d.toLocaleString("en-US", { month: "short" }) + " " + d.getDate();
  });
}

// Find the soft booking (camelCase fields from backend) that covers a given week.
function getSoftBookingForWeek(member, weekIndex) {
  if (!member.availabilityDate || !member.softBookings?.length) return null;
  const weekStart = new Date(member.availabilityDate + "T00:00:00");
  weekStart.setDate(weekStart.getDate() + weekIndex * 7);
  return (
    member.softBookings.find(sb => {
      if (!sb.endDate) return false;
      return new Date(sb.endDate + "T00:00:00") > weekStart;
    }) || null
  );
}

// Format a soft booking as "Client · Project" (or whichever is available).
function sbLabel(sb) {
  if (!sb) return "";
  const c = sb.client || "";
  const p = sb.project || "";
  if (c && p) return `${c} · ${p}`;
  return c || p || "Soft booking";
}

// Build tooltip text for a capacity cell.
function cellTooltip(type, member, weekIndex) {
  if (type === "soft") {
    const sb = getSoftBookingForWeek(member, weekIndex);
    if (!sb) return null;
    const pct = sb.winProbability != null ? ` (${sb.winProbability}% close)` : "";
    return `${sbLabel(sb)}${pct}`;
  }
  if (type === "unavailable") return member.reason || null;
  return null;
}

const sampleData = {
  score: 8.1,
  projectStart: "2024-08-26",
  durationWeeks: 12,
  teamReadiness: "HIGH",
  averageDelay: 3.3,
  csot4Week: 94.2,
  members: [
    {
      name: "Todd Korando",
      role: "DevOps Engineer",
      availabilityDate: "2024-08-26",
      delayDays: 0,
      effectiveDelayDays: 0,
      isSoftDelayed: false,
      reason: "Available immediately",
      fteEquivalent: 1.0,
      weeklyCapacity: [100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100],
      weeklyCapacityType: Array(12).fill("available"),
      csot4Week: 96,
      csot4WeekConfirmed: 96,
      csot12Week: 88,
      softBookings: [],
      risks: [],
    },
    {
      name: "Shirley Cortez",
      role: "SQL Developer",
      availabilityDate: "2024-08-26",
      delayDays: 7,
      effectiveDelayDays: 7,
      isSoftDelayed: false,
      reason: "Wrapping current engagement (legacy data cleanup)",
      fteEquivalent: 1.0,
      weeklyCapacity: [0, 50, 100, 100, 100, 100, 100, 100, 100, 100, 100, 100],
      weeklyCapacityType: ["unavailable", "available", ...Array(10).fill("available")],
      csot4Week: 91,
      csot4WeekConfirmed: 91,
      csot12Week: 78,
      softBookings: [],
      risks: ["7-day ramp delay — limited week 1 capacity"],
    },
  ],
  scheduleRisks: [
    { severity: "low", text: "Plan contingency for Legacy Cleanup · Data Corp if Shirley is assigned here — 0% chance of backfill obligation." },
  ],
};

function getCapacityColor(pct, type) {
  if (type === "soft")        return COLORS.amber;
  if (type === "unavailable") return COLORS.red;
  return pct >= 70 ? COLORS.green : pct >= 40 ? COLORS.amber : COLORS.red;
}


function MemberAvailabilityCard({ member, projectStart, onEmployeeClick }) {
  const weekLabels = computeWeekLabels(member.availabilityDate || projectStart);


  return (
    <div style={{ background: BG_BASE, borderRadius: 8, border: `1px solid ${COLORS.cardBorder}`, padding: "12px 16px", marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>
            <ClickableName name={member.name} onClick={onEmployeeClick} />
          </div>
          <div style={{ fontSize: 10, color: COLORS.textMuted }}>{member.role}</div>
        </div>
        <div />
      </div>

      <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 10, fontStyle: "italic" }}>{member.reason}</div>

      {/* Soft booking detail rows */}
      {(member.softBookings || []).length > 0 && (
        <div style={{ marginBottom: 10 }}>
          {member.softBookings.map((sb, i) => {
            const wp = sb.winProbability ?? null;
            const c = wp != null && wp >= 60 ? COLORS.red : wp != null && wp >= 30 ? COLORS.amber : COLORS.green;
            return (
              <div key={i} style={{ fontSize: 10, padding: "5px 8px", borderRadius: 4, background: `${c}10`, border: `1px solid ${c}33`, marginBottom: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ color: COLORS.text }}>{sbLabel(sb)}{sb.status ? ` · ${sb.status}` : ""}</span>
                <span style={{ fontWeight: 700, color: c, whiteSpace: "nowrap", marginLeft: 8 }}>
                  {wp != null ? `${wp}% close` : "prob unknown"} · ends {sb.endDate || "?"}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginBottom: member.risks?.length > 0 ? 8 : 0 }}>
        <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Weekly Capacity</div>
        <div style={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
          {(member.weeklyCapacity || []).map((cap, i) => {
            const type = (member.weeklyCapacityType || [])[i] || "available";
            const tooltip = cellTooltip(type, member, i);
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <div
                  title={tooltip || undefined}
                  style={{ width: "100%", height: Math.max(4, (cap / 100) * 32), background: getCapacityColor(cap, type), borderRadius: "2px 2px 0 0", opacity: 0.85, cursor: tooltip ? "help" : "default" }}
                />
                <span style={{ fontSize: 7, color: COLORS.textMuted }}>{weekLabels[i]}</span>
              </div>
            );
          })}
        </div>
        {member.isSoftDelayed && (
          <div style={{ fontSize: 9, color: COLORS.amber, marginTop: 4 }}>
            Amber bars = soft-booked period. If the deal closes, backfill will be needed for those weeks.
          </div>
        )}
      </div>

      {(member.risks || []).map((r, i) => (
        <div key={i} style={{ fontSize: 10, color: COLORS.amber, marginTop: 6, padding: "4px 8px", background: `${COLORS.amber}08`, borderRadius: 3, borderLeft: `2px solid ${COLORS.amber}` }}>
          ⚠ {r}
        </div>
      ))}
    </div>
  );
}

function OverviewCapacityCell({ value, type, tooltip }) {
  const color = getCapacityColor(value, type);
  return (
    <div
      title={tooltip || undefined}
      style={{ flex: 1, textAlign: "center", background: `${color}${type === "soft" ? "30" : "20"}`, borderRadius: 3, padding: "4px 2px", border: type === "soft" ? `1px solid ${color}55` : "none", cursor: tooltip ? "help" : "default" }}
    >
      <span style={{ fontSize: 8, fontWeight: 600, color }}>{type === "soft" ? "SB" : `${value}%`}</span>
    </div>
  );
}

export default function AvailabilityView({ data, onEmployeeClick }) {
  const d = data || sampleData;
  const scoreColor = d.score >= 7 ? COLORS.green : d.score >= 5 ? COLORS.amber : COLORS.red;
  const readinessConfig = {
    HIGH:   { color: COLORS.green, bg: `${COLORS.green}20` },
    MEDIUM: { color: COLORS.amber, bg: `${COLORS.amber}20` },
    LOW:    { color: COLORS.red,   bg: `${COLORS.red}20`   },
  }[d.teamReadiness] || { color: COLORS.textMuted, bg: "#ffffff10" };

  // Anchor the overview to the earliest role start, not the opportunity date.
  // data.projectStart is a sales/planning field that may not match when the
  // team actually boots up. The minimum availabilityDate across members is
  // the true engagement start for capacity planning purposes.
  const timelineStart = (d.members || []).reduce((earliest, m) => {
    if (!m.availabilityDate) return earliest;
    return !earliest || m.availabilityDate < earliest ? m.availabilityDate : earliest;
  }, null) || d.projectStart;

  const overviewLabels = computeWeekLabels(timelineStart);

  return (
    <div style={{ background: COLORS.cardBg, border: `1px solid ${COLORS.cardBorder}`, borderRadius: 8, padding: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: COLORS.text, margin: 0, textTransform: "uppercase", letterSpacing: 1 }}>Availability & Timing</h3>
          {d.projectStart && (
            <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 3 }}>
              Target start: <span style={{ color: COLORS.text }}>{d.projectStart}</span>
              {d.durationWeeks && <span> · {d.durationWeeks}-week engagement</span>}
            </div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 22, fontWeight: 700, color: scoreColor }}>{d.score}</span>
          {d.teamReadiness && (
            <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: readinessConfig.bg, color: readinessConfig.color }}>{d.teamReadiness} READINESS</span>
          )}
        </div>
      </div>

      {/* Summary stats */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Days to Full Capacity", value: (() => { const max = Math.max(0, ...(d.members || []).filter(m => !m.isSoftDelayed).map(m => m.delayDays || 0)); return max === 0 ? "Ready Now" : `${max}d`; })(), color: (() => { const max = Math.max(0, ...(d.members || []).filter(m => !m.isSoftDelayed).map(m => m.delayDays || 0)); const hasSoft = (d.members || []).some(m => m.isSoftDelayed); return max > 14 ? COLORS.red : max > 0 ? COLORS.amber : hasSoft ? COLORS.amber : COLORS.green; })() },
          { label: "Team Ready",  value: (d.members?.filter(m => m.delayDays === 0 || m.isSoftDelayed).length || 0) + "/" + (d.members?.length || 0) + " manageable", color: COLORS.text },
        ].map((item, i) => (
          <div key={i} style={{ background: BG_BASE, borderRadius: 6, padding: "10px 12px" }}>
            <div style={{ fontSize: 9, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1 }}>{item.label}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: item.color, marginTop: 4 }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Team Gantt overview */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Team Capacity Overview</div>
        <div style={{ background: BG_BASE, borderRadius: 6, padding: "12px 14px" }}>
          <div style={{ display: "flex", gap: 2, marginBottom: 6 }}>
            <div style={{ minWidth: 130 }} />
            {overviewLabels.map((lbl, i) => (
              <div key={i} style={{ flex: 1, textAlign: "center", fontSize: 8, color: COLORS.textMuted }}>{lbl}</div>
            ))}
          </div>
          {(d.members || []).map((m, mi) => {
            const offsetWeeks = timelineStart && m.availabilityDate
              ? Math.max(0, Math.round(
                  (new Date(m.availabilityDate + "T00:00:00") - new Date(timelineStart + "T00:00:00"))
                  / (7 * 24 * 3600 * 1000)
                ))
              : 0;
            const visibleWeeks = 12 - offsetWeeks;

            // First initial + last name, e.g. "J. Parham"
            const parts = m.name.split(" ");
            const shortName = parts.length > 1
              ? `${parts[0][0]}. ${parts[parts.length - 1]}`
              : parts[0];

            return (
              <div key={mi} style={{ display: "flex", gap: 2, alignItems: "center", marginBottom: 4 }}>
                <div style={{ minWidth: 130, fontSize: 10, color: COLORS.text, paddingRight: 8 }}>{shortName}</div>

                {Array.from({ length: offsetWeeks }, (_, k) => (
                  <div key={`pre-${k}`} style={{ flex: 1, minWidth: 28, padding: "4px 2px", borderRadius: 3, border: "1px solid transparent", background: "transparent" }} />
                ))}

                {(m.weeklyCapacity || []).slice(0, visibleWeeks).map((cap, wi) => {
                  const type = (m.weeklyCapacityType || [])[wi] || "available";
                  const tooltip = cellTooltip(type, m, wi);
                  return <OverviewCapacityCell key={wi} value={cap} type={type} tooltip={tooltip} />;
                })}
              </div>
            );
          })}
        </div>
      </div>

      {/* Individual cards */}
      <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Member Availability Detail</div>
      {(d.members || []).map((m, i) => (
        <MemberAvailabilityCard key={i} member={m} projectStart={d.projectStart} onEmployeeClick={onEmployeeClick} />
      ))}

      {/* Schedule risks */}
      {(d.scheduleRisks || []).length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Schedule Risk Assessment</div>
          {d.scheduleRisks.map((r, i) => {
            const sev = { high: COLORS.red, medium: COLORS.amber, low: COLORS.green }[r.severity] || COLORS.textMuted;
            return (
              <div key={i} style={{ display: "flex", gap: 10, padding: "8px 12px", borderRadius: 4, background: BG_BASE, borderLeft: `3px solid ${sev}`, marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: sev, flexShrink: 0, textTransform: "uppercase" }}>{r.severity}</span>
                <span style={{ fontSize: 11, color: COLORS.text, lineHeight: 1.5 }}>{r.text}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
